/**
 * Path/URL normalization for observability labels.
 *
 * High-cardinality request paths (UUIDs, long numeric IDs, order/store/user ids,
 * TIN/CNPJ, phone numbers, query strings) explode the number of series in Grafana.
 * These helpers collapse dynamic segments into a stable `[id]` template so labels
 * stay bounded. Pure functions — safe to use on server and client.
 */

const UUID = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
/** 7+ consecutive digits: order/store ids, CNPJ (14), TIN, phone, barcodes. */
const LONG_NUMERIC_ID = /\d{7,}/g;

/**
 * Normalize a path: strip query string and fragment, then templatize UUIDs and
 * long numeric ids.
 *
 * `/api/v1/store/97e7…/order/2527…/status?tin=01738930000100`
 * → `/api/v1/store/[id]/order/[id]/status`
 */
export function normalizePath(path: string): string {
  return path
    .replace(/#.*$/, '')
    .replace(/\?.*$/, '')
    .replace(UUID, '[id]')
    .replace(LONG_NUMERIC_ID, '[id]');
}

/**
 * Normalize a full or relative URL down to its normalized pathname. Query string
 * and fragment are dropped. Falls back to {@link normalizePath} on the raw input
 * when the value cannot be parsed as a URL.
 */
export function normalizeUrl(url: string): string {
  try {
    const { pathname } = new URL(url, 'http://localhost');
    return normalizePath(pathname);
  } catch {
    return normalizePath(url);
  }
}
